import { theme, type ThemeConfig } from "antd";

export type AppThemeName = "light" | "dark";

export const THEME_STORAGE_KEY = "app_theme";

const commonTheme: ThemeConfig = {
  token: {
    borderRadius: 12,
    wireframe: false,
    fontSize: 16,
    fontFamily:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
  },
  components: {
    Button: {
      borderRadius: 999,
      controlHeight: 50,
      fontWeight: 700,
    },
    Card: {
      borderRadiusLG: 16,
    },
    Input: {
      controlHeight: 50,
      borderRadius: 12,
    },
    InputNumber: {
      controlHeight: 50,
      borderRadius: 12,
    },
    Select: {
      controlHeight: 50,
      borderRadius: 12,
    },
    DatePicker: {
      controlHeight: 50,
      borderRadius: 12,
    },
  },
};

const lightTheme: ThemeConfig = {
  ...commonTheme,
  algorithm: theme.defaultAlgorithm,
  token: {
    ...commonTheme.token,
    colorPrimary: "#16d4e0",
    colorInfo: "#16d4e0",
    colorSuccess: "#52c41a",
    colorWarning: "#faad14",
    colorError: "#ff4d4f",
    colorTextBase: "#1f2937",
    colorBgBase: "#f3f5f9",
  },
  components: {
    ...commonTheme.components,
    Input: {
      controlHeight: 50,
      borderRadius: 12,
      colorBgContainer: "#ffffff80",
    },
    Select: {
      controlHeight: 50,
      borderRadius: 12,
      colorBgContainer: "#ffffff80",
    },
    InputNumber: {
      controlHeight: 50,
      borderRadius: 12,
      colorBgContainer: "#ffffff80",
    },
    DatePicker: {
      controlHeight: 50,
      borderRadius: 12,
      colorBgContainer: "#ffffff80",
    },
  },
};

const darkTheme: ThemeConfig = {
  ...commonTheme,
  algorithm: theme.darkAlgorithm,
  token: {
    ...commonTheme.token,
    colorPrimary: "#3ee7ed",
    colorInfo: "#3ee7ed",
    colorSuccess: "#39d353",
    colorError: "#f85149",
    colorWarning: "#d29922",
    colorTextBase: "#e6edf3",
    colorBgBase: "#0d1117",
    colorBgContainer: "#161b22",
    colorBgElevated: "#161b22",
    colorBorder: "#21262d",
    colorBorderSecondary: "#21262d",
    colorTextSecondary: "#8b949e",
    colorTextTertiary: "#6e7681",
    borderRadius: 8,
  },
  components: {
    ...commonTheme.components,
    Button: {
      borderRadius: 10,
      controlHeight: 50,
      fontWeight: 700,
    },
    Card: {
      borderRadiusLG: 12,
      headerBg: "transparent",
      bodyPadding: 16,
      colorBorderSecondary: "#21262d",
      colorBgContainer: "#161b22",
      boxShadow: "none",
    },
    Input: {
      controlHeight: 50,
      borderRadius: 8,
      colorBgContainer: "#0d1117",
      colorBorder: "#21262d",
      colorText: "#e6edf3",
      colorTextPlaceholder: "#484f58",
      activeBorderColor: "#3ee7ed",
      hoverBorderColor: "#30363d",
      activeShadow: "0 0 0 2px rgba(62,231,237,0.15)",
    },
    Select: {
      controlHeight: 50,
      borderRadius: 8,
      colorBgContainer: "#0d1117",
      colorBorder: "#21262d",
      colorText: "#e6edf3",
      colorTextPlaceholder: "#484f58",
      optionSelectedBg: "#1f2937",
      activeBorderColor: "#3ee7ed",
      hoverBorderColor: "#30363d",
      activeOutlineColor: "rgba(62,231,237,0.15)",
    },
    InputNumber: {
      controlHeight: 50,
      borderRadius: 8,
      colorBgContainer: "#0d1117",
      colorBorder: "#21262d",
      colorText: "#e6edf3",
      activeBorderColor: "#3ee7ed",
      hoverBorderColor: "#30363d",
      activeShadow: "0 0 0 2px rgba(62,231,237,0.15)",
    },
    DatePicker: {
      controlHeight: 50,
      borderRadius: 8,
      colorBgContainer: "#0d1117",
      colorBorder: "#21262d",
      colorText: "#e6edf3",
      colorTextPlaceholder: "#484f58",
      activeBorderColor: "#3ee7ed",
      hoverBorderColor: "#30363d",
      activeShadow: "0 0 0 2px rgba(62,231,237,0.15)",
    },
    Switch: {
      colorPrimary: "#3ee7ed",
      colorPrimaryHover: "#3ee7ed",
      colorTextQuaternary: "#21262d",
      handleBg: "#ffffff",
    },
  },
};

export const appThemes: Record<AppThemeName, ThemeConfig> = {
  light: lightTheme,
  dark: darkTheme,
};

export function resolveInitialTheme(): AppThemeName {
  const saved = localStorage.getItem(THEME_STORAGE_KEY);
  if (saved === "light" || saved === "dark") return saved;
  return "light";
}
